import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Download, Upload, RefreshCw, RotateCcw, Clock, KeyRound, Copy, MessageCircle } from "lucide-react";
import { api, inputCls, labelCls, NCF_TYPES, askPin } from "./posShared";

export default function Configuracion({ settings, reloadSettings }) {
  const [s, setS] = useState(settings || {});
  const [pin, setPin] = useState("");
  const [ncf, setNcf] = useState([]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const deliveryList = s.delivery_options || [];
  const setDelivery = (arr) => set("delivery_options", arr);
  const addDelivery = () => setDelivery([...deliveryList, { label: "", price: "" }]);
  const updDelivery = (i, k, v) => setDelivery(deliveryList.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const delDelivery = (i) => setDelivery(deliveryList.filter((_, idx) => idx !== i));

  useEffect(() => { setS(settings || {}); }, [settings]);
  const loadNcf = useCallback(() => api.get("/pos/ncf").then((r) => setNcf(r.data)).catch(() => {}), []);
  useEffect(() => { loadNcf(); }, [loadNcf]);

  const [codes, setCodes] = useState([]);
  const [genQty, setGenQty] = useState(1);
  const [genPct, setGenPct] = useState(10);
  const [genExpiry, setGenExpiry] = useState(0);
  const loadCodes = useCallback(() => api.get("/pos/otc").then((r) => setCodes(r.data)).catch(() => {}), []);
  useEffect(() => { loadCodes(); }, [loadCodes]);
  const genCodes = async (category) => {
    try {
      const { data } = await api.post("/pos/otc/generate", {
        category, count: Number(genQty) || 1,
        percent: category === "descuento" ? Number(genPct) || 0 : 0,
        expires_hours: Number(genExpiry) || 0,
      });
      toast.success(`${data.length} código(s) generado(s)`);
      loadCodes();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar"); }
  };
  const delCode = async (id) => {
    try { await api.delete(`/pos/otc/${id}`); loadCodes(); } catch (e) { toast.error("No se pudo eliminar"); }
  };
  const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success(`Código ${c} copiado`); };
  const sendWhatsApp = (c) => {
    const num = window.prompt("Número de WhatsApp del cajero (con código de país, ej: 18095551234):", "");
    if (num === null) return;
    const clean = (num || "").replace(/[^0-9]/g, "");
    const pctTxt = c.category === "descuento" && c.percent ? ` de ${c.percent}% de descuento` : " de autorización";
    const msg = encodeURIComponent(`Código${pctTxt} de un solo uso para MO by Vir: ${c.code}`);
    window.open(`https://wa.me/${clean}?text=${msg}`, "_blank");
  };
  const catLabel = { descuento: "Descuento", autorizacion: "Autorización (devoluciones y cierre)" };
  const fmtExp = (c) => {
    if (!c.expires_at) return "";
    const exp = new Date(c.expires_at);
    const vencido = exp < new Date();
    return vencido ? " · VENCIDO" : ` · vence ${c.expires_at.slice(0, 16).replace("T", " ")}`;
  };

  const save = async () => {
    const body = { ...s };
    if (pin) body.pin = pin;
    try {
      await api.put("/pos/settings", body);
      toast.success("Configuración guardada");
      setPin("");
      reloadSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [nf, setNf] = useState({ ncf_type: "consumo", prefix: "B02", current: 1, end: 100, active: true });
  const addNcf = async () => {
    const p = await askPin();
    try {
      await api.post("/pos/ncf", { ...nf, current: Number(nf.current), end: Number(nf.end), pin: p });
      toast.success("Secuencia NCF creada");
      loadNcf();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delNcf = async (id) => {
    const p = await askPin();
    try { await api.delete(`/pos/ncf/${id}?pin=${encodeURIComponent(p)}`); toast.success("Eliminada"); loadNcf(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const fileRef = useRef(null);
  const [restoring, setRestoring] = useState(false);
  const [backups, setBackups] = useState([]);
  const loadBackups = useCallback(() => api.get("/pos/backups").then((r) => setBackups(r.data)).catch(() => {}), []);
  useEffect(() => { loadBackups(); }, [loadBackups]);

  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/pos/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `respaldo-pos-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch (e) { toast.error("No se pudo generar el respaldo"); }
  };
  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Restaurar reemplazará TODOS los datos actuales del POS (productos, clientes, facturas, inventario). ¿Continuar?")) return;
    const pin = await askPin();
    if (!pin) return;
    setRestoring(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await api.post("/pos/restore", { data: parsed, pin });
      toast.success("Respaldo restaurado. Recargando...");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archivo inválido o error al restaurar");
    } finally { setRestoring(false); }
  };

  const runBackupNow = async () => {
    try {
      await api.post("/pos/backups/run");
      toast.success("Respaldo creado");
      loadBackups();
    } catch (e) { toast.error("No se pudo crear el respaldo"); }
  };
  const downloadSnapshot = async (b) => {
    try {
      const { data } = await api.get(`/pos/backups/${b.id}`);
      const blob = new Blob([JSON.stringify(data.data || data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `respaldo-pos-${(b.created_at || "").slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar"); }
  };
  const restoreSnapshot = async (b) => {
    if (!window.confirm("Restaurar este respaldo reemplazará TODOS los datos actuales del POS. ¿Continuar?")) return;
    const pin = await askPin();
    if (!pin) return;
    try {
      await api.post(`/pos/backups/${b.id}/restore`, { pin });
      toast.success("Respaldo restaurado. Recargando...");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al restaurar"); }
  };
  const deleteSnapshot = async (b) => {
    if (!window.confirm("¿Eliminar este respaldo?")) return;
    const pin = await askPin();
    try {
      await api.delete(`/pos/backups/${b.id}?pin=${encodeURIComponent(pin)}`);
      toast.success("Respaldo eliminado");
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-serif text-3xl mb-6">Configuración</h1>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
        <h2 className="font-medium">Datos del negocio</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <F l="Nombre del negocio" testid="pos-cfg-name" v={s.business_name} on={(v) => set("business_name", v)} />
          <F l="RNC" v={s.rnc} on={(v) => set("rnc", v)} />
          <F l="Teléfono" v={s.phone} on={(v) => set("phone", v)} />
          <F l="WhatsApp" v={s.whatsapp} on={(v) => set("whatsapp", v)} />
          <F l="Correo" v={s.email} on={(v) => set("email", v)} />
          <F l="Logo (URL)" v={s.logo} on={(v) => set("logo", v)} />
          <div className="sm:col-span-2"><label className={labelCls}>Dirección</label><input value={s.address || ""} onChange={(e) => set("address", e.target.value)} className={inputCls} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Mensaje de agradecimiento</label><input value={s.thank_you_message || ""} onChange={(e) => set("thank_you_message", e.target.value)} className={inputCls} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Política de cambios y devoluciones</label><input value={s.return_policy || ""} onChange={(e) => set("return_policy", e.target.value)} className={inputCls} /></div>
        </div>

        <h2 className="font-medium pt-2 border-t border-neutral-100">Facturación e impuestos</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <F l="Tasa ITBIS (%)" testid="pos-cfg-itbis" type="number" v={s.itbis_rate} on={(v) => set("itbis_rate", v)} />
          <F l="Prefijo de factura" v={s.invoice_prefix} on={(v) => set("invoice_prefix", v)} />
          <F l="Descuento máx. (%)" type="number" v={s.max_discount_percent} on={(v) => set("max_discount_percent", v)} />
        </div>

        <h2 className="font-medium pt-2 border-t border-neutral-100">Seguridad</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <F l="Nuevo PIN de administrador" testid="pos-cfg-pin" v={s.admin_pin} on={(v) => set("admin_pin", v)} />
          <F l="PIN actual (para cambios protegidos)" testid="pos-cfg-currentpin" v={pin} on={setPin} />
        </div>
        <p className="text-xs text-neutral-400">Cambiar ITBIS o el PIN requiere ingresar el PIN actual en el campo de la derecha.</p>

        <h2 className="font-medium pt-2 border-t border-neutral-100">Envío automático de factura</h2>
        <p className="text-xs text-neutral-400 -mt-2">Al cerrar una venta con un cliente registrado, la factura se envía automáticamente por los canales activados (si el cliente tiene ese dato).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm"><input data-testid="pos-cfg-autoemail" type="checkbox" checked={!!s.auto_send_email} onChange={(e) => set("auto_send_email", e.target.checked)} /> Enviar por correo</label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="pos-cfg-autowa" type="checkbox" checked={!!s.auto_send_whatsapp} onChange={(e) => set("auto_send_whatsapp", e.target.checked)} /> Abrir WhatsApp con la factura</label>
        </div>

        <button data-testid="pos-cfg-save" onClick={save} className="bg-espresso text-cream rounded-full px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Guardar configuración</button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 mt-6">
        <h2 className="font-medium mb-1">Tarifas de delivery</h2>
        <p className="text-xs text-neutral-400 mb-4">Define precios fijos de envío. El cajero podrá seleccionarlos en Facturar y se sumarán al total <strong>después del ITBIS</strong>.</p>
        <div className="space-y-2 mb-3">
          {deliveryList.map((o, i) => (
            <div key={i} data-testid={`pos-delivery-row-${i}`} className="grid grid-cols-[1fr_150px_auto] gap-2 items-end">
              <div><label className={labelCls}>Nombre / Zona</label>
                <input data-testid={`pos-delivery-label-${i}`} value={o.label || ""} onChange={(e) => updDelivery(i, "label", e.target.value)} placeholder="Ej: Santo Domingo Centro" className={inputCls} /></div>
              <div><label className={labelCls}>Precio (RD$)</label>
                <input data-testid={`pos-delivery-price-${i}`} type="number" value={o.price ?? ""} onChange={(e) => updDelivery(i, "price", e.target.value)} className={inputCls} /></div>
              <button data-testid={`pos-delivery-del-${i}`} onClick={() => delDelivery(i)} className="p-2.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={14} /></button>
            </div>
          ))}
          {deliveryList.length === 0 && <p className="text-neutral-400 text-sm">Sin tarifas de delivery. Añade una para habilitar la opción en Facturar.</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="pos-delivery-add" onClick={addDelivery} className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] hover:border-espresso transition"><Plus size={14} /> Añadir tarifa</button>
          <button data-testid="pos-delivery-save" onClick={save} className="inline-flex items-center gap-1.5 bg-espresso text-cream rounded-full px-5 py-2 text-xs uppercase tracking-[0.12em] hover:bg-rose transition">Guardar tarifas</button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 mt-6" data-testid="pos-otc-card">
        <h2 className="font-medium mb-1 flex items-center gap-2"><KeyRound size={16} /> Códigos de autorización de un solo uso</h2>
        <p className="text-xs text-neutral-400 mb-4">Genera códigos aleatorios de 7 caracteres. Cada código sirve <strong>una sola vez</strong>. El cajero los usa en lugar del PIN maestro para autorizar operaciones en tu ausencia.</p>
        <div className="flex flex-wrap items-end gap-3 mb-2">
          <div><label className={labelCls}>Cantidad</label>
            <input data-testid="pos-otc-qty" type="number" min={1} max={50} value={genQty} onChange={(e) => setGenQty(e.target.value)} className={`${inputCls} w-24`} /></div>
          <div><label className={labelCls}>% descuento</label>
            <input data-testid="pos-otc-pct" type="number" min={1} max={100} value={genPct} onChange={(e) => setGenPct(e.target.value)} className={`${inputCls} w-28`} /></div>
          <div><label className={labelCls}>Caduca en (horas, 0 = nunca)</label>
            <input data-testid="pos-otc-expiry" type="number" min={0} value={genExpiry} onChange={(e) => setGenExpiry(e.target.value)} className={`${inputCls} w-44`} /></div>
        </div>
        <div className="flex flex-wrap gap-3 mb-5">
          <button data-testid="pos-otc-gen-descuento" onClick={() => genCodes("descuento")} className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] hover:border-espresso transition"><Plus size={14} /> Generar descuento ({genPct || 0}%)</button>
          <button data-testid="pos-otc-gen-autorizacion" onClick={() => genCodes("autorizacion")} className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] hover:border-espresso transition"><Plus size={14} /> Generar autorización</button>
        </div>
        {["descuento", "autorizacion"].map((cat) => {
          const list = codes.filter((c) => c.category === cat);
          return (
            <div key={cat} className="mb-4">
              <h3 className="text-xs uppercase tracking-[0.12em] text-neutral-500 mb-2">{catLabel[cat]} <span className="text-neutral-300">({list.filter((c) => !c.used).length} disponibles)</span></h3>
              {list.length === 0 && <p className="text-sm text-neutral-400">Sin códigos.</p>}
              <div className="grid sm:grid-cols-2 gap-2">
                {list.map((c) => (
                  <div key={c.id} data-testid={`pos-otc-item-${c.code}`} className={`flex items-center justify-between border rounded-lg px-3 py-2 ${c.used ? "border-neutral-100 bg-neutral-50 text-neutral-400" : "border-neutral-200"}`}>
                    <div>
                      <span className="font-mono font-semibold tracking-wider">{c.code}</span>
                      {cat === "descuento" && <span className="ml-2 text-[11px] text-rose">{c.percent}%</span>}
                      {c.used
                        ? <span className="ml-2 text-[11px]">Usado{c.used_by ? ` · ${c.used_by}` : ""}</span>
                        : <span className="ml-2 text-[11px] text-emerald-600">Disponible{fmtExp(c)}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {!c.used && <button onClick={() => sendWhatsApp(c)} title="Enviar por WhatsApp" className="p-1.5 text-neutral-400 hover:text-emerald-600"><MessageCircle size={14} /></button>}
                      {!c.used && <button onClick={() => copyCode(c.code)} title="Copiar" className="p-1.5 text-neutral-400 hover:text-espresso"><Copy size={14} /></button>}
                      <button data-testid={`pos-otc-del-${c.code}`} onClick={() => delCode(c.id)} title="Eliminar" className="p-1.5 text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 mt-6">
        <h2 className="font-medium mb-4">Secuencias de comprobantes fiscales (NCF)</h2>
        <div className="space-y-2 mb-4">
          {ncf.map((n) => (
            <div key={n.id} className="flex items-center justify-between text-sm border-b border-neutral-50 py-2">
              <div>
                <span className="font-medium capitalize">{n.ncf_type.replace("_", " ")}</span>
                <span className="text-neutral-500"> · {n.prefix} · próximo {n.prefix}{String(n.current).padStart(8, "0")}</span>
                {n.remaining <= 5 && <span className="ml-2 inline-flex items-center gap-1 text-amber-600 text-xs"><AlertTriangle size={11} /> Quedan {n.remaining}</span>}
              </div>
              <button onClick={() => delNcf(n.id)} className="text-neutral-400 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
          {ncf.length === 0 && <p className="text-neutral-400 text-sm">Sin secuencias registradas.</p>}
        </div>
        <div className="grid sm:grid-cols-5 gap-2 items-end">
          <div><label className={labelCls}>Tipo</label><select value={nf.ncf_type} onChange={(e) => setNf({ ...nf, ncf_type: e.target.value })} className={inputCls}>{NCF_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
          <div><label className={labelCls}>Prefijo</label><input value={nf.prefix} onChange={(e) => setNf({ ...nf, prefix: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Desde</label><input type="number" value={nf.current} onChange={(e) => setNf({ ...nf, current: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Hasta</label><input type="number" value={nf.end} onChange={(e) => setNf({ ...nf, end: e.target.value })} className={inputCls} /></div>
          <button data-testid="pos-add-ncf" onClick={addNcf} className="inline-flex items-center justify-center gap-1 bg-espresso text-cream rounded-lg py-2.5 text-xs uppercase hover:bg-rose transition"><Plus size={14} /> Añadir</button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl p-6 mt-6">
        <h2 className="font-medium mb-1">Copia de seguridad</h2>
        <p className="text-xs text-neutral-400 mb-4">Descarga un respaldo completo (productos, clientes, facturas, inventario y configuración) o restaura desde un archivo. La restauración requiere el PIN de administrador y reemplaza los datos actuales.</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="pos-backup-download" onClick={downloadBackup} className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition"><Download size={15} /> Descargar respaldo</button>
          <button data-testid="pos-backup-restore" onClick={() => fileRef.current?.click()} disabled={restoring} className="inline-flex items-center gap-2 border border-espresso text-espresso rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-espresso hover:text-cream transition disabled:opacity-60"><Upload size={15} /> {restoring ? "Restaurando..." : "Restaurar respaldo"}</button>
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={restoreBackup} className="hidden" />
        </div>

        <div className="mt-6 pt-5 border-t border-neutral-100">
          <h3 className="font-medium mb-3 flex items-center gap-2"><Clock size={16} className="text-rose" /> Respaldo automático</h3>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input data-testid="pos-auto-backup-toggle" type="checkbox" checked={!!s.auto_backup_enabled} onChange={(e) => set("auto_backup_enabled", e.target.checked)} />
            Activar respaldo automático
          </label>
          <div className="grid sm:grid-cols-2 gap-4 max-w-md">
            <div>
              <label className={labelCls}>Frecuencia</label>
              <select data-testid="pos-auto-backup-freq" value={s.auto_backup_frequency_hours ?? 24} onChange={(e) => set("auto_backup_frequency_hours", Number(e.target.value))} className={inputCls}>
                <option value={6}>Cada 6 horas</option>
                <option value={12}>Cada 12 horas</option>
                <option value={24}>Diario (24 h)</option>
                <option value={168}>Semanal</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Respaldos a conservar</label>
              <input type="number" min={1} value={s.auto_backup_keep ?? 10} onChange={(e) => set("auto_backup_keep", Number(e.target.value))} className={inputCls} />
            </div>
          </div>
          <p className="text-xs text-neutral-400 mt-2">Guarda automáticamente según la frecuencia. Recuerda pulsar «Guardar configuración» para aplicar los cambios.</p>

          <div className="flex items-center justify-between mt-4 mb-2">
            <span className="text-sm font-medium">Respaldos guardados</span>
            <button data-testid="pos-backup-run" onClick={runBackupNow} className="inline-flex items-center gap-1.5 text-xs border border-neutral-200 rounded-full px-3 py-1.5 hover:border-espresso transition"><RefreshCw size={13} /> Crear ahora</button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {backups.map((b) => (
              <div key={b.id} data-testid={`pos-backup-row-${b.id}`} className="flex items-center justify-between text-sm border-b border-neutral-50 py-2">
                <div>
                  <div className="font-medium text-espresso">{(b.created_at || "").slice(0, 16).replace("T", " ")}
                    <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded-full ${b.kind === "auto" ? "bg-rose/10 text-rose" : "bg-neutral-100 text-neutral-500"}`}>{b.kind === "auto" ? "Automático" : "Manual"}</span>
                  </div>
                  <div className="text-xs text-neutral-400">{Object.values(b.counts || {}).reduce((a, c) => a + c, 0)} registros · {b.counts?.pos_invoices ?? 0} facturas</div>
                </div>
                <div className="flex gap-1.5">
                  <button data-testid={`pos-backup-dl-${b.id}`} onClick={() => downloadSnapshot(b)} title="Descargar" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Download size={13} /></button>
                  <button data-testid={`pos-backup-restore-${b.id}`} onClick={() => restoreSnapshot(b)} title="Restaurar" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><RotateCcw size={13} /></button>
                  <button data-testid={`pos-backup-del-${b.id}`} onClick={() => deleteSnapshot(b)} title="Eliminar" className="p-1.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
            {backups.length === 0 && <p className="text-neutral-400 text-sm py-4 text-center">Aún no hay respaldos guardados.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

const F = ({ l, v, on, type = "text", testid }) => (
  <div><label className={labelCls}>{l}</label><input data-testid={testid} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className={inputCls} /></div>
);
